import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';

const buildMonetizationSchema = (lng) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Como monetizamos - FinMaple',
  description: 'FinMaple genera ingresos a traves de enlaces de afiliados. Nuestras recomendaciones son editorialmente independientes.',
  url: 'https://www.finmaple.com/como-monetizamos',
  publisher: {
    '@type': 'Organization',
    name: 'FinMaple',
    url: 'https://www.finmaple.com',
  },
  inLanguage: lng,
  availableLanguage: ['es', 'en', 'fr'],
});

const ComoMonetizamosPage = () => {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="howWeMonetize"
        title={t('pages.monetization.seoTitle')}
        description={t('pages.monetization.seoDescription')}
        canonical="/como-monetizamos"
        schema={buildMonetizationSchema(i18n.language)}
        routeKey="howWeMonetize"
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title={t('pages.monetization.title')}
        subtitle={t('pages.monetization.subtitle')}
        breadcrumbs={[{ label: t('pages.monetization.breadcrumb') }]}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
        >
          {/* Section 1 */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.monetization.modelTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.modelParagraph1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.modelParagraph2')}
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 2 */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.monetization.independenceTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.independenceParagraph1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.independenceParagraph2')}
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 3 */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.monetization.transparencyTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.transparencyParagraph1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.transparencyCommitment')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {t('pages.monetization.commitmentItem1')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {t('pages.monetization.commitmentItem2')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {t('pages.monetization.commitmentItem3')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {t('pages.monetization.commitmentItem4')}
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section: FinMaple Score Methodology */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.monetization.scoreTitle', 'Metodología del FinMaple Score')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.scoreIntro', 'El FinMaple Score es una calificación de 1.0 a 5.0 que asignamos a cada producto financiero para ayudarte a comparar opciones de forma objetiva. No aceptamos pagos para mejorar el score de ningún producto.')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 1, fontWeight: 600 }}>
            {t('pages.monetization.scorePillarsTitle', 'Los 5 pilares del FinMaple Score:')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t('pages.monetization.scorePillar1Label', 'Costos (30%)')}</strong> — {t('pages.monetization.scorePillar1Desc', 'Cuotas mensuales, anuales y cargos ocultos.')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t('pages.monetization.scorePillar2Label', 'Accesibilidad newcomer (25%)')}</strong> — {t('pages.monetization.scorePillar2Desc', 'Facilidad de apertura sin historial crediticio canadiense.')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t('pages.monetization.scorePillar3Label', 'Experiencia digital (15%)')}</strong> — {t('pages.monetization.scorePillar3Desc', 'Calidad de la app, banca en línea y funciones digitales.')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t('pages.monetization.scorePillar4Label', 'Beneficios (15%)')}</strong> — {t('pages.monetization.scorePillar4Desc', 'Cashback, recompensas, bonos de bienvenida y tasas de interés.')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t('pages.monetization.scorePillar5Label', 'Soporte en español (15%)')}</strong> — {t('pages.monetization.scorePillar5Desc', 'Disponibilidad de servicio al cliente y materiales en español.')}
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 4 */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            {t('pages.monetization.trustTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('pages.monetization.trustParagraph')}
          </Typography>
        </Box>

        {/* Disclaimer */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, fontStyle: 'italic' }}>
            {t('pages.monetization.disclaimer')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ComoMonetizamosPage;
